import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import { scrollToId, PHONE_DISPLAY, PHONE_TEL } from "@/lib/api";

const links = [
  { href: "#kalkulacka", label: "Kalkulačka" },
  { href: "#proces", label: "Ako to funguje" },
  { href: "#referencie", label: "Referencie" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close the "Viac" dropdown when clicking anywhere else.
  useEffect(() => {
    if (!moreOpen) return;
    const close = () => setMoreOpen(false);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [moreOpen]);

  const go = (href) => {
    setOpen(false);
    scrollToId(href);
  };

  return (
    <motion.header
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-black/70 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-20 flex items-center justify-between gap-6">
        <Link to="/" data-testid="logo" className="flex items-center gap-2">
          <span className="font-display text-2xl md:text-3xl tracking-wide text-white">ZATEPLENIA</span>
          <span className="font-display text-2xl md:text-3xl tracking-wide text-gold">.EU</span>
        </Link>

        <nav className="hidden xl:flex items-center gap-4">
          {links.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              data-testid={`nav-${l.href.slice(1)}`}
              className="text-xs text-white/70 hover:text-white transition-colors font-mono2 uppercase tracking-wider whitespace-nowrap"
            >
              {l.label}
            </button>
          ))}
          <Link to="/blog" data-testid="nav-blog" className="text-xs text-white/70 hover:text-white transition-colors font-mono2 uppercase tracking-wider whitespace-nowrap">Blog</Link>
          <Link to="/faq" data-testid="nav-faq" className="text-xs text-white/70 hover:text-white transition-colors font-mono2 uppercase tracking-wider whitespace-nowrap">FAQ</Link>
          <Link to="/vizualizacia" data-testid="nav-vizualizacia" className="text-xs text-gold hover:text-gold-light transition-colors font-mono2 uppercase tracking-wider whitespace-nowrap">Vizualizácia</Link>
          <Link to="/galeria" data-testid="nav-galeria" className="text-xs text-white/70 hover:text-white transition-colors font-mono2 uppercase tracking-wider whitespace-nowrap">Galéria</Link>
          <Link to="/premeny" data-testid="nav-premeny" className="text-xs text-white/70 hover:text-white transition-colors font-mono2 uppercase tracking-wider whitespace-nowrap">Premeny</Link>
          <Link to="/kontakt" data-testid="nav-kontakt" className="text-xs text-white/70 hover:text-white transition-colors font-mono2 uppercase tracking-wider whitespace-nowrap">Kontakt</Link>
          <div className="relative">
            <button
              onClick={(e) => { e.stopPropagation(); setMoreOpen(!moreOpen); }}
              data-testid="nav-more-toggle"
              className="flex items-center gap-1 text-xs text-white/70 hover:text-white transition-colors font-mono2 uppercase tracking-wider whitespace-nowrap"
            >
              Viac <ChevronDown className={`w-3 h-3 transition-transform ${moreOpen ? "rotate-180" : ""}`} />
            </button>
            {moreOpen && (
              <div data-testid="nav-more-menu" className="absolute right-0 top-full mt-3 w-52 bg-ink border border-white/10 shadow-2xl py-2">
                <button onClick={() => { setMoreOpen(false); go("#o-nas"); }} data-testid="nav-o-nas" className="block w-full text-left px-4 py-2.5 text-xs text-white/70 hover:text-white hover:bg-white/5 font-mono2 uppercase tracking-wider">O nás</button>
                <Link to="/partneri" onClick={() => setMoreOpen(false)} data-testid="nav-partneri" className="block px-4 py-2.5 text-xs text-white/70 hover:text-white hover:bg-white/5 font-mono2 uppercase tracking-wider">Naši partneri</Link>
                <Link to="/partner" onClick={() => setMoreOpen(false)} data-testid="nav-partner" className="block px-4 py-2.5 text-xs text-white/70 hover:text-white hover:bg-white/5 font-mono2 uppercase tracking-wider">Pre partnerov</Link>
              </div>
            )}
          </div>
          <a href={`tel:${PHONE_TEL}`} data-testid="header-phone" className="flex items-center gap-2 text-sm text-gold hover:text-gold-light font-bold whitespace-nowrap">
            <Phone className="w-4 h-4" /> <span className="hidden 2xl:inline">{PHONE_DISPLAY}</span>
          </a>
          <button
            onClick={() => go("#kalkulacka")}
            data-testid="header-cta"
            className="bg-gold text-ink px-5 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-gold-light transition-colors whitespace-nowrap"
          >
            Vypočítať cenu
          </button>
        </nav>

        <button className="xl:hidden text-white" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="xl:hidden bg-ink border-t border-white/10 px-5 py-6 flex flex-col gap-4">
          {links.map((l) => (
            <button key={l.href} onClick={() => go(l.href)} className="text-left text-white/80 uppercase tracking-widest text-sm">
              {l.label}
            </button>
          ))}
          <button onClick={() => go("#o-nas")} className="text-left text-white/80 uppercase tracking-widest text-sm">O nás</button>
          <Link to="/blog" onClick={() => setOpen(false)} className="text-left text-white/80 uppercase tracking-widest text-sm">Blog</Link>
          <Link to="/faq" onClick={() => setOpen(false)} className="text-left text-white/80 uppercase tracking-widest text-sm">FAQ</Link>
          <Link to="/vizualizacia" onClick={() => setOpen(false)} className="text-left text-gold uppercase tracking-widest text-sm">Vizualizácia</Link>
          <Link to="/galeria" onClick={() => setOpen(false)} className="text-left text-white/80 uppercase tracking-widest text-sm">Galéria</Link>
          <Link to="/premeny" onClick={() => setOpen(false)} className="text-left text-white/80 uppercase tracking-widest text-sm">Premeny</Link>
          <Link to="/partneri" onClick={() => setOpen(false)} className="text-left text-white/80 uppercase tracking-widest text-sm">Naši partneri</Link>
          <Link to="/kontakt" onClick={() => setOpen(false)} className="text-left text-white/80 uppercase tracking-widest text-sm">Kontakt</Link>
          <Link to="/partner" onClick={() => setOpen(false)} className="text-left text-white/80 uppercase tracking-widest text-sm">Pre partnerov</Link>
          <a href={`tel:${PHONE_TEL}`} data-testid="mobile-phone" className="flex items-center gap-2 text-gold font-bold text-base py-1"><Phone className="w-4 h-4" /> {PHONE_DISPLAY}</a>
          <button onClick={() => go("#kalkulacka")} className="bg-gold text-ink px-6 py-3 font-bold uppercase tracking-wider text-sm">
            Vypočítať cenu
          </button>
        </div>
      )}
    </motion.header>
  );
}
